/**
 * AuditComplianceReportPage — `/reports/audit-compliance`.
 *
 * Audit-chain integrity per service. Phase 4c already wired
 * `GET /v1/audit/integrity/all` for the security dashboard — this report
 * surface reuses the same shape with date-range filtering when the report
 * engine seeds the definition.
 */
import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { verifyAllChains, type IntegrityReport } from '../../lib/api/audit';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

export function AuditComplianceReportPage(): JSX.Element {
  const report = findPreBuiltReport('audit-compliance');

  const integrity = useQuery<readonly IntegrityReport[], Error>({
    queryKey: ['audit', 'integrity', 'all'],
    queryFn: () => verifyAllChains(),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const denied =
    integrity.isError && integrity.error instanceof ApiError &&
    (integrity.error.status === 401 || integrity.error.status === 403);

  const reports = integrity.data ?? [];
  const okChains = reports.filter((r) => r.ok).length;
  const breaks = reports.reduce((acc, r) => acc + r.breaks.length, 0);

  return (
    <ReportShell report={report}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Chains verified"
          value={reports.length === 0 ? '—' : reports.length.toLocaleString('en-US')}
          hint="Audit integrity"
          icon={ShieldCheck}
        />
        <KPICard
          title="Chains OK"
          value={reports.length === 0 ? '—' : okChains.toLocaleString('en-US')}
          hint="hash + prevHash match"
        />
        <KPICard
          title="Chain breaks"
          value={reports.length === 0 ? '—' : breaks.toLocaleString('en-US')}
          tone={breaks > 0 ? 'danger' : 'success'}
        />
        <KPICard title="Retention pass" value="—" hint="Audit retention" />
      </div>

      {integrity.isPending ? (
        <p className="text-sm text-muted">Verifying chains…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">AuditIntegrity/verify</code> required.
        </DeferredNotice>
      ) : integrity.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {integrity.error.message}
        </DeferredNotice>
      ) : (
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Per-chain integrity</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Chain</th>
                  <th className="text-right px-4 py-2">Rows</th>
                  <th className="text-right px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Breaks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {reports.map((r) => (
                  <tr key={r.chainId}>
                    <td className="px-4 py-2 font-mono text-xs">{r.chainId}</td>
                    <td className="px-4 py-2 text-right text-xs">{r.totalRows.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2 text-right">
                      <StatusBadge status={r.ok ? 'COMPLETED' : 'FAILED'} />
                    </td>
                    <td className="px-4 py-2 text-right text-xs">{r.breaks.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportShell>
  );
}
